<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const authStore = useAuthStore()
const open = ref(false)
const isAuthenticated = computed(() => authStore.isAuthenticated)
const user = computed(() => authStore.user)

const authedLinks = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/connect', label: 'Connect' },
  { to: '/grants', label: 'Agents' },
  { to: '/audit', label: 'Audit' },
  { to: '/settings', label: 'Settings' },
]

const publicLinks = [
  { to: '/for-agents', label: 'For agents' },
  { to: '/security', label: 'Security' },
  { to: '/support', label: 'Support' },
]

async function handleLogout() {
  await authStore.logout()
  router.push('/')
}
</script>

<template>
  <header class="sticky top-0 z-20 border-b border-slate-200/80 bg-white/90 backdrop-blur">
    <nav class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div class="flex h-16 items-center justify-between">
        <RouterLink to="/" class="flex items-center gap-3">
          <span class="grid h-9 w-9 place-items-center rounded-xl bg-slate-950 text-sm font-black text-white">MR</span>
          <span class="text-lg font-bold tracking-tight text-slate-950">MoneyRelay</span>
        </RouterLink>

        <button class="rounded-lg border border-slate-200 px-3 py-2 text-sm md:hidden" @click="open = !open">
          Menu
        </button>

        <div class="hidden items-center gap-5 md:flex">
          <template v-if="isAuthenticated">
            <RouterLink
              v-for="link in authedLinks"
              :key="link.to"
              :to="link.to"
              class="text-sm font-medium text-slate-600 transition-colors hover:text-emerald-700"
              active-class="text-emerald-700"
            >
              {{ link.label }}
            </RouterLink>
            <span class="max-w-[180px] truncate text-xs text-slate-500">{{ user?.email }}</span>
            <button class="btn btn-secondary text-sm" @click="handleLogout">Logout</button>
          </template>
          <template v-else>
            <RouterLink
              v-for="link in publicLinks"
              :key="link.to"
              :to="link.to"
              class="text-sm font-medium text-slate-600 hover:text-emerald-700"
            >
              {{ link.label }}
            </RouterLink>
            <RouterLink to="/login" class="text-sm font-medium text-slate-600 hover:text-emerald-700">Login</RouterLink>
            <RouterLink to="/register" class="btn btn-primary">Get started</RouterLink>
          </template>
        </div>
      </div>

      <div v-if="open" class="space-y-2 border-t border-slate-100 py-4 md:hidden">
        <template v-if="isAuthenticated">
          <RouterLink
            v-for="link in authedLinks"
            :key="link.to"
            :to="link.to"
            class="block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            @click="open = false"
          >
            {{ link.label }}
          </RouterLink>
          <button class="btn btn-secondary w-full" @click="handleLogout">Logout</button>
        </template>
        <template v-else>
          <RouterLink
            v-for="link in publicLinks"
            :key="link.to"
            :to="link.to"
            class="block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            @click="open = false"
          >
            {{ link.label }}
          </RouterLink>
          <RouterLink to="/login" class="block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Login</RouterLink>
          <RouterLink to="/register" class="btn btn-primary w-full">Get started</RouterLink>
        </template>
      </div>
    </nav>
  </header>
</template>
